import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { getCertificatePreview, downloadCertificate, getLectureHistoryPreview, downloadLectureHistory } from '../../services/mypageApi';

interface CertificateModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'completion' | 'certificate';
  title?: string;
  personCertificationId?: number;
}

const CertificateModal: React.FC<CertificateModalProps> = ({ 
  isOpen, 
  onClose, 
  type,
  title,
  personCertificationId
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // body 스크롤 방지
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // 이수내역/수료증 미리보기 로드
  useEffect(() => {
    const loadPreview = async () => {
      if (isOpen) {
        setIsLoading(true);
        try {
          let blob: Blob;
          if (type === 'completion') {
            // 이수내역 미리보기
            blob = await getLectureHistoryPreview();
          } else if (personCertificationId && type === 'certificate') {
            // 수료증 미리보기
            blob = await getCertificatePreview(personCertificationId);
          } else {
            setIsLoading(false);
            return;
          }
          
          const url = URL.createObjectURL(blob);
          setPreviewUrl(url);
        } catch (error) {
          /// console.error('미리보기 로드 실패:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    // 이전 URL 정리
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }

    loadPreview();
  }, [isOpen, personCertificationId, type]);

  // 컴포넌트 언마운트 시 URL 정리
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // 이수내역/수료증 다운로드 처리
  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      let blob: Blob;
      let filename: string;

      if (type === 'completion') {
        // 이수내역 다운로드
        blob = await downloadLectureHistory();
        filename = '이수내역.pdf';
      } else if (personCertificationId) {
        // 수료증 다운로드
        blob = await downloadCertificate(personCertificationId);
        filename = `수료증_${title || '수료증'}.pdf`;
      } else {
        return;
      }
      
      // 파일 다운로드
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      /// console.error('다운로드 실패:', error);
      alert('다운로드에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setIsDownloading(false);
    }
  };

  if (!isOpen) return null;

  const modalTitle = type === 'completion' ? '이수 내역' : '수료증';

  const modalContent = (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        {/* 헤더 */}
        <ModalHeader>
          <ModalTitle>{modalTitle}</ModalTitle>
          <CloseButton onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="#171719"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </CloseButton>
        </ModalHeader>

        {/* 컨텐츠 */}
        <ModalContent>
          <CertificatePreview>
            {isLoading ? (
              <LoadingContent>
                <LoadingSpinner />
                <LoadingText>미리보기를 불러오는 중...</LoadingText>
              </LoadingContent>
            ) : previewUrl ? (
              <PreviewImage src={previewUrl} alt="수료증 미리보기" />
            ) : (
              <PlaceholderContent>
                증명서 미리보기
              </PlaceholderContent>
            )}
          </CertificatePreview>
        </ModalContent>

        {/* 하단 버튼 */}
        <ModalFooter>
          <DownloadButton 
            onClick={handleDownload}
            disabled={isDownloading || (type === 'certificate' && !personCertificationId)}
          >
            {isDownloading ? '발급 중...' : '발급하기 (PDF)'}
          </DownloadButton>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );

  // Portal을 사용해서 body에 직접 렌더링
  return ReactDOM.createPortal(modalContent, document.body);
};

// 스타일 컴포넌트들
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(23, 23, 25, 0.52);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  padding: 20px;
`;

const ModalContainer = styled.div`
  width: 560px;
  max-width: 1060px;
  min-width: 320px;
  max-height: 760px;
  background: white;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  position: relative;
  backdrop-filter: blur(32px);
  padding: 24px 28px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-bottom: 1px solid rgba(112, 115, 124, 0.16);
`;

const ModalTitle = styled.h2`
  color: black;
  font-size: 17px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  margin: 0;
  text-align: center;
  flex: 1;
`;

const CloseButton = styled.button`
  position: absolute;
  right: 28px;
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }
`;

const ModalContent = styled.div`
  padding: 32px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const CertificatePreview = styled.div`
  height: 240px;
  background: transparent;
  border-radius: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #448181;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  overflow: hidden;
`;

const LoadingContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const LoadingSpinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid rgba(68, 129, 129, 0.2);
  border-top: 3px solid #448181;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.span`
  font-size: 14px;
  color: #448181;
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const PlaceholderContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const ModalFooter = styled.div`
  padding: 24px 32px;
  border-top: 1px solid rgba(112, 115, 124, 0.16);
`;

const DownloadButton = styled.button`
  width: 100%;
  padding: 12px 28px;
  background: ${props => props.disabled ? '#ccc' : '#448181'};
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;

export default CertificateModal;